import React, { createElement, Fragment, useEffect, useState, useMemo } from 'react';
import Entry from '../position-modules/Entry';
import { stringToNumber } from '../../special/numberConverter';
import {
  Box,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { tokens } from '../../../global/theme/tokens';
import { useSortable } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';

export const PositionsTimeBilling = ({
  props,
  onChange,
  onFocus,
  item,
  items,
  index,
  columns,
  showVars,
  descriptionVars,
  imageType,
  imageApiFilter,
  collapseModules,
  openAmount,
  onCreate,
  className,
  onDelete,
  onEditTimeBilling,
  isDragging,
  timeTrackingData,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [expand, setExpand] = useState(item.expand || false);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    if (isDragging && isHovering) setIsHovering(false);
  }, [isDragging]);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.sortId });

  // change
  function handleChange(e, col) {
    var newItems = items;
    if (col.number) {
      newItems[index][col.key] = stringToNumber(
        e.target.value,
        col.decimalPlaces ? col.decimalPlaces : 2
      );
    } else {
      newItems[index][col.key] = e.target.value;
    }
    onChange([...newItems]);
  }

  function handleUnitChange(e) {
    var newItems = items;
    newItems[index].unit = e.target.value;
    onChange([...newItems]);
  }

  function handleDefaultChange(value, key) {
    var newItems = items;
    newItems[index][key] = value;
    onChange([...newItems]);
  }

  function toggleExpand() {
    handleChange({ target: { value: !expand } }, { key: 'expand' });
    setExpand(!expand);
  }

  // Get expected amount from wage preset (calculated from time entries)
  const expectedAmount = useMemo(() => {
    if (!timeTrackingData?.billingConfig?.wagePresets || !item.wagePresetId) {
      return null;
    }
    // Use String comparison to avoid type mismatch (number vs string)
    const preset = timeTrackingData.billingConfig.wagePresets.find(
      (wp) => String(wp.id) === String(item.wagePresetId)
    );
    return preset?.hours ?? null;
  }, [timeTrackingData, item.wagePresetId]);

  // Check if amount differs from expected (rounded to 2 decimal places)
  const hasAmountDiscrepancy = useMemo(() => {
    if (expectedAmount === null) return false;
    const roundedAmount = Math.round(parseFloat(item.amount) * 100) / 100;
    const roundedExpected = Math.round(expectedAmount * 100) / 100;
    return roundedAmount !== roundedExpected;
  }, [item.amount, expectedAmount]);

  return (
    <Fragment>
      <tr
        {...props}
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        style={{
          transition,
          cursor: 'pointer',
          backgroundColor: hasAmountDiscrepancy ? colors.warning : undefined,
        }}
        className={className + ' ' + (item.orderId ? 'order-position' : '')}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {collapseModules && (
          <td>
            <IconButton onClick={() => toggleExpand()}>
              <ExpandMoreIcon
                sx={{
                  transform: `rotate(${expand ? '-180deg' : '0deg'})`,
                  transition: '0.2s',
                }}
              />
            </IconButton>
          </td>
        )}
        {columns.map((col, i) => {
          return (
            <Fragment key={'Fragment' + item.position + i}>
              {col.key == 'delete' ? (
                <td>
                  <Box className="flex items-center gap-1">
                    {hasAmountDiscrepancy && (
                      <Tooltip
                        title={`${t('Amount deviates from time entries')}: ${t('Calculated')}: ${expectedAmount?.toFixed(2)} ${t('h')}, ${t('Entered')}: ${parseFloat(item.amount).toFixed(2)} ${t('h')}`}
                      >
                        <WarningAmberIcon
                          fontSize="small"
                          sx={{ color: colors.warning }}
                        />
                      </Tooltip>
                    )}
                    {onEditTimeBilling && (
                      <Tooltip title={t('Edit Time Entries')}>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditTimeBilling(item);
                          }}
                          size="small"
                          sx={{
                            color: colors.blueAccent?.[400] || '#2196f3',
                          }}
                        >
                          <AccessTimeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton
                      className={`${
                        isHovering && !isDragging && onDelete
                          ? 'visible'
                          : 'invisible'
                      }`}
                      onClick={() => onDelete(item)}
                    >
                      <DeleteIcon sx={{ color: 'red' }} />
                    </IconButton>
                  </Box>
                </td>
              ) : (
                <Entry
                  key={'' + item.position + i}
                  imageType={imageType}
                  imageApiFilter={imageApiFilter}
                  col={col}
                  item={items[index]}
                  onFocus={(ref) => onFocus(ref, col.key)}
                  onChange={(e) => handleChange(e, col)}
                  onUnitChange={handleUnitChange}
                >
                  {item[col.key]}
                </Entry>
              )}
            </Fragment>
          );
        })}
      </tr>
      {collapseModules && (
        <tr className={className}>
          <td colSpan={999}>
            {expand && (
              <Collapse in={expand} className="px-6">
                <Box
                  className="w-full flex flex-col gap-2 p-4 rounded-lg"
                  sx={{
                    borderColor: colors.selected,
                    borderWidth: '1px',
                  }}
                >
                  {/* Standard collapse modules */}
                  <Box className="w-full grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {collapseModules.map((module, modIdx) =>
                      createElement(module.component, {
                        key: item.id + 'module' + modIdx,
                        openAmount,
                        imageType,
                        imageApiFilter,
                        position: item,
                        data: item[module.key],
                        moduleKey: module.key,
                        onFocus: (ref) => onFocus(ref, module.key),
                        unit: module.unit,
                        label: module.label,
                        defaultValue: module.value,
                        decimalPlaces: module.decimalPlaces,
                        descriptionVars,
                        showVars,
                        onChange: (value, manuelKey) =>
                          handleDefaultChange(value, manuelKey || module.key),
                        onCreate,
                        textBlockType: module.textBlockType,
                      })
                    )}
                  </Box>
                </Box>
              </Collapse>
            )}
          </td>
        </tr>
      )}
    </Fragment>
  );
};
